/**
 * Turn pixels into Observations, then collapse one overpass into one group.
 * Eight NOAA-21 pixels in the same 5-minute pass are one thermal group, not eight votes.
 */

import { POLAR_NOMINAL_MW } from "./event-state";
import type { Detection, Observation, SensorObservationGroup } from "./types";

const MIN_5 = 5 * 60 * 1000;
const MIN_10 = 10 * 60 * 1000;

/** Approx geolocation error (m). Used later for association, not for counting. */
export const UNCERTAINTY_M: Record<string, number> = {
  VIIRS_SNPP: 375,
  VIIRS_NOAA20: 375,
  VIIRS_NOAA21: 375,
  MODIS: 1000,
  MTG: 1000,
  S3: 1000,
};

export function uncertaintyM(source: string): number {
  return UNCERTAINTY_M[source] ?? 1000;
}

/** Polar overpasses are ~5 min; MTG FRP ListProduct is a 10-min slot. */
export function passBucketMs(source: string): number {
  return source === "MTG" ? MIN_10 : MIN_5;
}

export function passBucketIso(iso: string, source: string): string {
  const step = passBucketMs(source);
  const floored = Math.floor(Date.parse(iso) / step) * step;
  return new Date(floored).toISOString().replace(".000Z", "Z");
}

export function toObservation(d: Detection): Observation {
  return {
    ...d,
    kind: d.kind ?? "thermal",
    uncertainty_m: d.uncertainty_m ?? uncertaintyM(d.source),
  };
}

/** Group key is platform + pass — NOAA-21 and S-NPP in the same hour stay independent. */
export function groupKey(o: Observation): string {
  return `${o.source}|${passBucketIso(o.observed_at, o.source)}`;
}

export function groupObservations(detections: Detection[]): SensorObservationGroup[] {
  const buckets = new Map<string, Observation[]>();
  for (const d of detections) {
    if (d.source === "seed") continue;
    const o = toObservation(d);
    if (o.kind !== "thermal") continue;
    const key = groupKey(o);
    const list = buckets.get(key) ?? [];
    list.push(o);
    buckets.set(key, list);
  }

  const groups: SensorObservationGroup[] = [];
  for (const members of buckets.values()) {
    members.sort((a, b) => a.observed_at.localeCompare(b.observed_at));
    let frp_sum = 0;
    let frp_max = 0;
    let confidence_max = 0;
    let sumLat = 0;
    let sumLon = 0;
    let bt_diff_max: number | null = null;
    let sza_min: number | null = null;
    for (const m of members) {
      const frp = m.frp_mw ?? 0;
      frp_sum += frp;
      if (frp > frp_max) frp_max = frp;
      if (m.confidence > confidence_max) confidence_max = m.confidence;
      sumLat += m.lat;
      sumLon += m.lon;
      // Keep the hottest ΔT and the highest-sun pixel — quality, not a vote.
      if (m.bt_diff_k != null && (bt_diff_max == null || m.bt_diff_k > bt_diff_max)) {
        bt_diff_max = m.bt_diff_k;
      }
      if (m.sza_deg != null && (sza_min == null || m.sza_deg < sza_min)) {
        sza_min = m.sza_deg;
      }
    }
    const first = members[0]!;
    groups.push({
      platform: first.source,
      pass_bucket: passBucketIso(first.observed_at, first.source),
      observed_at: first.observed_at,
      lat: sumLat / members.length,
      lon: sumLon / members.length,
      n: members.length,
      frp_sum,
      frp_max,
      confidence_max,
      uncertainty_m: first.uncertainty_m,
      bt_diff_max,
      sza_min,
    });
  }

  groups.sort((a, b) => a.observed_at.localeCompare(b.observed_at) || a.platform.localeCompare(b.platform));
  return groups;
}

/** Polar pinpricks do not justify a class; MTG is never that floor. */
function lookWeight(g: SensorObservationGroup): number {
  // S3 has no FRP from EFFIS — still keep the look in the panel log.
  const real = g.platform === "MTG" || g.platform === "S3" || g.frp_max >= POLAR_NOMINAL_MW;
  return (real ? 1_000_000 : 0) + g.frp_max;
}

/**
 * Newest-first, but keep each platform's best look even when it is days old.
 * Otherwise Details can say two satellites while the log only shows recent MTG.
 */
export function selectObservationLog(groups: SensorObservationGroup[], limit = 12): SensorObservationGroup[] {
  if (groups.length <= limit) {
    return groups.slice().sort((a, b) => Date.parse(b.observed_at) - Date.parse(a.observed_at));
  }
  const newest = groups.reduce((a, b) => (Date.parse(a.observed_at) >= Date.parse(b.observed_at) ? a : b));
  const bestByPlat = new Map<string, SensorObservationGroup>();
  for (const g of groups) {
    const cur = bestByPlat.get(g.platform);
    if (!cur || lookWeight(g) > lookWeight(cur)) bestByPlat.set(g.platform, g);
  }
  const pinned = new Set<SensorObservationGroup>([...bestByPlat.values(), newest]);
  const byNew = (a: SensorObservationGroup, b: SensorObservationGroup) =>
    Date.parse(b.observed_at) - Date.parse(a.observed_at);
  const head = [...pinned].sort(byNew);
  const rest = groups.filter((g) => !pinned.has(g)).sort(byNew);
  return [...head, ...rest].slice(0, limit);
}
